/**
 * Shared auth session contract for Insitestore360 shell + SPFT + Shop Profiling.
 * All micro-frontends must read/write these keys so login and localStorage stay unified.
 */

export const AUTH_STORAGE_KEYS = {
  AUTH_TOKEN: 'auth-token',
  /** Legacy SPFT alias — keep in sync with AUTH_TOKEN. */
  ACCESS_TOKEN: 'accessToken',
  PROJECT_TOKENS: 'project-tokens',
  PROJECT_TOKENS_P1: 'project-tokens-p1',
  ACTIVE_PROJECT_ID: 'active-project-id',
  /** Set at login when user has Store360 / IntElux (project 1 or legacy null). */
  HAS_INTELUX_ACCESS: 'has-intelux-access',
  USER: 'user',
  USERNAME: 'username',
  PROFILE: 'profile',
  ID: 'id',
  ROLE_DETAILS: 'roleDetails',
  REDIRECT_URL: 'redirectUrl',
  LAST_VISITED_ROUTE: 'lastVisitedRoute',
} as const;

export type ProjectTokenEntry = {
  readonly projectId: number;
  readonly projectName: string;
  readonly appPath?: string;
  readonly token: string;
  readonly roles?: unknown[];
};

export type ProjectSwitcherItem = {
  readonly projectId: number | null;
  readonly label: string;
  readonly appPath: string;
  readonly isDefault?: boolean;
};

export const DEFAULT_APP_PATHS = {
  INTELUX: '/admin/landing-page',
  PM: '/admin/project-management/leads-summary',
  SPFT: '/admin/spft/psi-planning',
  SHOP_PROFILING: '/admin/shop-profiling/dashboard',
  LOGIN: '/login',
} as const;

const HOST_SESSION_KEYS: readonly string[] = [
  AUTH_STORAGE_KEYS.AUTH_TOKEN,
  AUTH_STORAGE_KEYS.ACCESS_TOKEN,
  AUTH_STORAGE_KEYS.PROJECT_TOKENS,
  AUTH_STORAGE_KEYS.PROJECT_TOKENS_P1,
  AUTH_STORAGE_KEYS.ACTIVE_PROJECT_ID,
  AUTH_STORAGE_KEYS.HAS_INTELUX_ACCESS,
  AUTH_STORAGE_KEYS.USER,
  AUTH_STORAGE_KEYS.USERNAME,
  AUTH_STORAGE_KEYS.PROFILE,
  AUTH_STORAGE_KEYS.ID,
  AUTH_STORAGE_KEYS.ROLE_DETAILS,
  'IsProjectManagement',
  'isProjectManager',
  'projectManagementRoleId',
  'email',
  'userEmail',
];

const DEFAULT_INTELUX_LABEL = 'IntElux';

/**
 * Store360 / IntElux roles use projectId 1, or legacy null/0 in the DB
 * (AuthService maps null → 0 and excludes them from response.projects).
 */
export function isStore360ProjectRole(role: {
  projectId?: number | null;
}): boolean {
  if (role.projectId == null) {
    return true;
  }
  const projectId = Number(role.projectId);
  return projectId === 0 || projectId === 1;
}

function decodeJwtPayload(token: string): { roles?: Array<{ id?: number }> } | null {
  try {
    const segment = token.split('.')[1];
    if (!segment || typeof atob !== 'function') {
      return null;
    }
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(normalized)) as { roles?: Array<{ id?: number }> };
  } catch {
    return null;
  }
}

/**
 * Backend omits Project 1 from response.projects (global JWT is IntElux).
 * Infer IntElux access when the global token has roles not covered by other
 * project tokens (PM/SPFT-only users share the same roles → no IntElux).
 */
export function inferInteluxAccessFromTokens(
  projects = readProjectTokens(),
  globalToken =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(AUTH_STORAGE_KEYS.PROJECT_TOKENS_P1) || getAuthToken()
      : null,
): boolean {
  if (projects.some(isInteluxProject)) {
    return true;
  }
  if (!globalToken) {
    return false;
  }
  const payload = decodeJwtPayload(globalToken);
  const globalRoles = (payload?.roles ?? []) as Array<{ id?: number; projectId?: number | null }>;
  if (globalRoles.length > 0) {
    const hasInteluxRoleInToken = globalRoles.some((role) => isStore360ProjectRole(role));
    if (!hasInteluxRoleInToken) {
      return false;
    }
  }

  // Toggle PM (project_management_user, no native project-2 token) ⇒ IntElux + PM
  if (
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('IsProjectManagement') === 'true' &&
    !projects.some(isPmProject)
  ) {
    return true;
  }

  const globalRoleIds = new Set(
    globalRoles
      .map((role) => Number(role?.id))
      .filter((id) => !!id),
  );
  if (!globalRoleIds.size) {
    return false;
  }
  const otherRoleIds = new Set(
    projects.flatMap((project) =>
      ((project.roles as Array<{ id?: number }> | undefined) ?? [])
        .map((role) => Number(role?.id))
        .filter((id) => !!id),
    ),
  );
  for (const roleId of globalRoleIds) {
    if (!otherRoleIds.has(roleId)) {
      return true;
    }
  }
  return false;
}

export type EnsureProjectTokenOptions = {
  globalToken?: string | null;
  roles?: Array<{ id?: number; name?: string; projectId?: number | null }>;
  /** True when user exists in master.project_management_user */
  hasPmAccess?: boolean;
  projectManagementRoleId?: number | null;
};

function resolveGlobalToken(explicit?: string | null): string | null {
  return (
    explicit ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem(AUTH_STORAGE_KEYS.PROJECT_TOKENS_P1)
      : null) ||
    getAuthToken()
  );
}

function hasNativePmRole(
  projects: ProjectTokenEntry[],
  roles: Array<{ projectId?: number | null }>,
): boolean {
  return (
    roles.some((role) => Number(role.projectId) === 2) ||
    projects.some(isPmProject)
  );
}

/**
 * Toggle-based PM users live in project_management_user and have IntElux roles
 * (no native projectId=2 role). They must get IntElux + PM in the switcher.
 *
 * Native PM users also get a project_management_user row (for role_id tracking)
 * — those must NOT be treated as IntElux+PM toggle users.
 */
export function isTogglePmDualAccessUser(
  projects: ProjectTokenEntry[],
  options?: EnsureProjectTokenOptions,
): boolean {
  if (!options?.hasPmAccess) {
    return false;
  }

  const optionsRoles = options?.roles ?? [];
  const projectRoles = projects.flatMap(
    (p) => (p.roles as Array<{ id?: number; projectId?: number }>) ?? [],
  );
  const roles = optionsRoles.length > 0 ? optionsRoles : projectRoles;

  // Prefer role projectIds — synthetic PM tokens must not mark the user native-PM.
  if (roles.some((role) => Number(role.projectId) === 2)) {
    return false;
  }
  if (roles.some(isStore360ProjectRole)) {
    return true;
  }
  if (projects.some(isInteluxProject)) {
    return true;
  }
  // No role metadata (e.g. header reload): dual when PM flag is set and there
  // is no real native PM token yet (or only our synthetic placeholder).
  const pmEntry = projects.find(isPmProject);
  if (!pmEntry) {
    return true;
  }
  const pmRoles = (pmEntry.roles as Array<{ name?: string }> | undefined) ?? [];
  const isSyntheticPm =
    pmRoles.length === 1 && pmRoles[0]?.name === 'Project Management';
  return isSyntheticPm;
}

/**
 * Ensure IntElux appears in project-tokens for hybrid / toggle-PM users.
 * Auth login only returns non-Project-1 entries in response.projects.
 */
export function ensureInteluxInProjectTokens(
  projects: ProjectTokenEntry[],
  options?: EnsureProjectTokenOptions,
): ProjectTokenEntry[] {
  if (projects.some(isInteluxProject)) {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(AUTH_STORAGE_KEYS.HAS_INTELUX_ACCESS, 'true');
    }
    return projects;
  }

  const optionsRoles = options?.roles ?? [];
  const projectRoles = projects.flatMap(
    (p) => (p.roles as Array<{ id?: number; projectId?: number }>) ?? [],
  );
  const roles = optionsRoles.length > 0 ? optionsRoles : projectRoles;

  const globalToken = resolveGlobalToken(options?.globalToken);
  const toggleDualAccess = isTogglePmDualAccessUser(projects, options);

  const hasExplicitInteluxRole = roles.some(
    (role) =>
      role.projectId != null &&
      (Number(role.projectId) === 1 || Number(role.projectId) === 0),
  );

  const hasOnlyNonInteluxRoles =
    roles.length > 0 &&
    roles.every(
      (role) => role.projectId != null && Number(role.projectId) > 1,
    );

  const hasIntelux =
    !hasOnlyNonInteluxRoles &&
    (hasExplicitInteluxRole ||
      toggleDualAccess ||
      roles.some(isStore360ProjectRole) ||
      inferInteluxAccessFromTokens(projects, globalToken));

  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(
      AUTH_STORAGE_KEYS.HAS_INTELUX_ACCESS,
      hasIntelux ? 'true' : 'false',
    );
  }

  if (!hasIntelux || !globalToken) {
    return projects;
  }

  const inteluxEntry: ProjectTokenEntry = {
    projectId: 1,
    projectName: DEFAULT_INTELUX_LABEL,
    appPath: DEFAULT_APP_PATHS.INTELUX,
    token: globalToken,
    roles: roles.filter(isStore360ProjectRole),
  };

  return [inteluxEntry, ...projects];
}

/**
 * Ensure project-tokens contains every app the user can open:
 * - IntElux (global JWT / project 1)
 * - PM when user is in project_management_user (toggle) or has native PM roles
 */
export function ensureSessionProjectTokens(
  projects: ProjectTokenEntry[],
  options?: EnsureProjectTokenOptions,
): ProjectTokenEntry[] {
  let next = ensureInteluxInProjectTokens(projects, options);
  const globalToken = resolveGlobalToken(options?.globalToken);
  const hasPmAccess =
    options?.hasPmAccess === true ||
    (typeof localStorage !== 'undefined' &&
      localStorage.getItem('IsProjectManagement') === 'true');
  const needsPm =
    hasPmAccess ||
    hasNativePmRole(next, options?.roles ?? []) ||
    next.some(isPmProject);

  if (!needsPm || next.some(isPmProject) || !globalToken) {
    return withNormalizedAppPaths(next);
  }

  // Toggle PM users have no projectId=2 JWT from login — reuse global token
  // (same as the legacy IntElux ↔ PM header toggle).
  const pmRoleId = Number(options?.projectManagementRoleId);
  const pmEntry: ProjectTokenEntry = {
    projectId: 2,
    projectName: 'Project Management',
    appPath: DEFAULT_APP_PATHS.PM,
    token: globalToken,
    roles: [
      {
        id: Number.isFinite(pmRoleId) && pmRoleId > 0 ? pmRoleId : 16,
        name: 'Project Management',
        projectId: 2,
      },
    ],
  };

  return withNormalizedAppPaths([...next, pmEntry]);
}

/** Persist relative appPath only — never bake localhost/prod hosts into storage. */
function withNormalizedAppPaths(
  projects: ProjectTokenEntry[],
): ProjectTokenEntry[] {
  return projects.map((project) => ({
    ...project,
    appPath: resolveProjectAppPath(project),
  }));
}

export function getAuthToken(): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  return (
    localStorage.getItem(AUTH_STORAGE_KEYS.AUTH_TOKEN) ||
    localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
  );
}

export function setAuthToken(token: string): void {
  localStorage.setItem(AUTH_STORAGE_KEYS.AUTH_TOKEN, token);
  localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, token);
}

export function readProjectTokens(): ProjectTokenEntry[] {
  const raw = localStorage.getItem(AUTH_STORAGE_KEYS.PROJECT_TOKENS);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as ProjectTokenEntry[]) : [];
  } catch {
    return [];
  }
}

/** Canonical SPFT project id in master.project / spft-service. */
export const SPFT_PROJECT_ID = 4;

export function isSpftProject(project: ProjectTokenEntry): boolean {
  return (
    Number(project.projectId) === SPFT_PROJECT_ID ||
    /spft|spf/i.test(project.projectName ?? '') ||
    (typeof project.appPath === 'string' && project.appPath.includes('spft'))
  );
}

/** True for embedded MFE shells that are not IntElux or Project Management. */
export function isStandaloneMfeProject(project: ProjectTokenEntry): boolean {
  return !isInteluxProject(project) && !isPmProject(project);
}

/** Route prefix used to detect whether the user is already inside a project shell. */
export function getProjectRoutePrefix(project: ProjectTokenEntry): string {
  if (isSpftProject(project)) {
    return '/admin/spft';
  }
  if (isShopProfilingProject(project)) {
    return '/admin/shop-profiling';
  }
  if (isPmProject(project)) {
    return '/admin/project-management';
  }
  const resolved = resolveProjectAppPath(project).split('?')[0] || DEFAULT_APP_PATHS.INTELUX;
  return resolved.replace(/\/$/, '') || DEFAULT_APP_PATHS.INTELUX;
}

/** Home path for an already-authenticated user (LoginGuard / session restore). */
export function resolveLoggedInHomePath(
  projects = readProjectTokens(),
): string {
  if (!projects.length) {
    return DEFAULT_APP_PATHS.INTELUX;
  }
  const activeProjectId = getActiveProjectId(projects);
  const active =
    projects.find((project) => project.projectId === activeProjectId) ??
    projects[0];
  return resolveProjectAppPath(active);
}

export function isShopProfilingProject(project: ProjectTokenEntry): boolean {
  return (
    /shop.?profil/i.test(project.projectName ?? '') ||
    (typeof project.appPath === 'string' &&
      project.appPath.includes('shop-profiling'))
  );
}

export function isInteluxProject(project: ProjectTokenEntry): boolean {
  return (
    Number(project.projectId) === 1 ||
    /intelux|intlux|store360|insitestore/i.test(project.projectName ?? '')
  );
}

export function isPmProject(project: ProjectTokenEntry): boolean {
  return (
    Number(project.projectId) === 2 ||
    /project.?management|^pm$/i.test(project.projectName ?? '')
  );
}

/**
 * Strip any host (localhost / prod) from master.project.app_path so navigation
 * always uses the current deployment origin. Prefer storing relative paths in
 * DB (e.g. `/admin/landing-page`).
 */
export function normalizeAppPath(
  path: string | undefined,
  fallback: string,
): string {
  let raw = path?.trim() || '';
  if (
    raw.startsWith('http://') ||
    raw.startsWith('https://') ||
    raw.startsWith('//')
  ) {
    try {
      const url = new URL(raw.startsWith('//') ? `https:${raw}` : raw);
      raw = url.pathname + url.search + url.hash;
    } catch {
      // keep raw if parsing fails
    }
  }
  if (raw && !raw.startsWith('/')) {
    raw = `/${raw}`;
  }
  const clean = raw.replace(/\/$/, '');
  if (
    !clean ||
    clean === '/' ||
    clean === '/admin' ||
    clean === '/admin/spft'
  ) {
    return fallback;
  }
  return raw;
}

/**
 * Build a same-origin absolute URL from a relative (or absolute) app path.
 * Uses `window.location.origin` so local and prod both pick the right host.
 */
export function toAppHref(
  path: string | undefined,
  fallback: string = DEFAULT_APP_PATHS.INTELUX,
): string {
  const relative = normalizeAppPath(path, fallback);
  if (typeof window === 'undefined' || !window.location?.origin) {
    return relative;
  }
  return `${window.location.origin}${relative}`;
}

export function resolveProjectAppPath(project: ProjectTokenEntry): string {
  if (isSpftProject(project)) {
    return normalizeAppPath(project.appPath, DEFAULT_APP_PATHS.SPFT);
  }
  if (isShopProfilingProject(project)) {
    return normalizeAppPath(project.appPath, DEFAULT_APP_PATHS.SHOP_PROFILING);
  }
  if (isPmProject(project)) {
    const path = normalizeAppPath(project.appPath, DEFAULT_APP_PATHS.PM);
    // A Trade Operation / landing path on the PM record would flash IntElux
    // before the shell corrects to Project Management.
    if (!path.includes('/project-management/')) {
      return DEFAULT_APP_PATHS.PM;
    }
    return path;
  }
  const path = normalizeAppPath(project.appPath, DEFAULT_APP_PATHS.INTELUX);
  if (isInteluxProject(project) && path.includes('/project-management/')) {
    return DEFAULT_APP_PATHS.INTELUX;
  }
  return path;
}

export function getActiveProjectId(projects = readProjectTokens()): number | null {
  const storedId = localStorage.getItem(AUTH_STORAGE_KEYS.ACTIVE_PROJECT_ID);
  if (storedId !== null && storedId !== '') {
    const parsedId = Number(storedId);
    if (!Number.isNaN(parsedId) && projects.some((p) => p.projectId === parsedId)) {
      return parsedId;
    }
  }

  const currentToken = getAuthToken();
  if (currentToken) {
    const matches = projects.filter((project) => project.token === currentToken);
    // Toggle PM reuses the IntElux JWT. Prefer Trade Operation when the user
    // has not explicitly selected another project.
    const intelux = matches.find(isInteluxProject);
    if (intelux) {
      return intelux.projectId;
    }
    if (matches.length) {
      return matches[0].projectId;
    }
  }

  const defaultProject = projects.find(isInteluxProject);
  return defaultProject?.projectId ?? null;
}

export function getProjectSwitcherItems(
  projects = readProjectTokens(),
): ProjectSwitcherItem[] {
  const items: ProjectSwitcherItem[] = [];
  const hasExplicitIntelux = projects.some(isInteluxProject);

  // Auth excludes Project 1 from response.projects — restore IntElux in the
  // switcher only when the user actually has Store360/IntElux access.
  if (!hasExplicitIntelux && inferInteluxAccessFromTokens(projects)) {
    items.push({
      projectId: null,
      label: DEFAULT_INTELUX_LABEL,
      appPath: DEFAULT_APP_PATHS.INTELUX,
      isDefault: true,
    });
  }

  for (const project of projects) {
    items.push({
      projectId: project.projectId,
      label: project.projectName,
      appPath: resolveProjectAppPath(project),
      isDefault: isInteluxProject(project),
    });
  }

  return items;
}

export function shouldShowProjectSwitcher(projects = readProjectTokens()): boolean {
  // Only show when the user can actually switch between 2+ projects.
  if (getProjectSwitcherItems(projects).length <= 1) {
    return false;
  }
  // Hide the header dropdown on Trade Operation. Users leave that project
  // from the side menu; the dropdown appears after they switch to PMT.
  return !isActiveTradeOperation(projects);
}

/** Project 1, or no explicit project yet (just after login). */
function isActiveTradeOperation(projects: ProjectTokenEntry[]): boolean {
  const activeId = getActiveProjectId(projects);
  if (activeId == null) {
    return true;
  }
  const active = projects.find((project) => project.projectId === activeId);
  return active ? isInteluxProject(active) : activeId === 1;
}

/**
 * Swap active project JWT and hard-navigate to that project's appPath.
 * Shared by IntElux header and SPFT header so both stay in sync.
 */
export function switchProject(projectId: number | null, appPath?: string): void {
  const projects = readProjectTokens();
  const activeId = getActiveProjectId(projects);
  if (activeId === projectId) {
    // Same project already active — still leave a mismatched PM page when
    // switching stays on IntElux (select re-pick / stale URL).
    const target = appPath || '';
    const onPmPage = window.location.pathname.includes('/project-management/');
    const switchingToIntelux =
      projectId === null ||
      projects.some((p) => p.projectId === projectId && isInteluxProject(p));
    if (switchingToIntelux && onPmPage) {
      localStorage.setItem('isProjectManager', 'false');
      const lastVisited = localStorage.getItem('lastVisitedRoute') || '';
      if (lastVisited.includes('/project-management/')) {
        localStorage.removeItem('lastVisitedRoute');
      }
      window.location.href = toAppHref(target || DEFAULT_APP_PATHS.INTELUX);
    }
    return;
  }

  if (projectId === null) {
    const defaultToken = localStorage.getItem(AUTH_STORAGE_KEYS.PROJECT_TOKENS_P1);
    if (defaultToken) {
      setAuthToken(defaultToken);
    } else {
      const intelux = projects.find(isInteluxProject);
      if (intelux?.token) {
        setAuthToken(intelux.token);
      }
    }
    localStorage.removeItem(AUTH_STORAGE_KEYS.ACTIVE_PROJECT_ID);
    localStorage.setItem('isProjectManager', 'false');
    const lastVisited = localStorage.getItem('lastVisitedRoute') || '';
    if (lastVisited.includes('/project-management/')) {
      localStorage.removeItem('lastVisitedRoute');
    }
    window.location.href = toAppHref(appPath || DEFAULT_APP_PATHS.INTELUX);
    return;
  }

  const entry = projects.find((project) => project.projectId === projectId);
  if (!entry?.token) {
    return;
  }

  if (activeId === null) {
    const currentToken = getAuthToken();
    if (currentToken) {
      localStorage.setItem(AUTH_STORAGE_KEYS.PROJECT_TOKENS_P1, currentToken);
    }
  }

  // Keep menu mode in sync with header project (PM vs IntElux).
  localStorage.setItem(
    'isProjectManager',
    isPmProject(entry) ? 'true' : 'false',
  );

  // IntElux: prefer the full global token (roleMapIds + union countries).
  // Project-shaped IntElux tokens break legacy product/store APIs after switch.
  if (isInteluxProject(entry)) {
    const globalToken = localStorage.getItem(AUTH_STORAGE_KEYS.PROJECT_TOKENS_P1);
    setAuthToken(globalToken || entry.token);
    localStorage.setItem(AUTH_STORAGE_KEYS.ACTIVE_PROJECT_ID, String(projectId));
    const lastVisited = localStorage.getItem('lastVisitedRoute') || '';
    if (lastVisited.includes('/project-management/')) {
      localStorage.removeItem('lastVisitedRoute');
    }
    window.location.href = toAppHref(
      appPath || resolveProjectAppPath(entry) || DEFAULT_APP_PATHS.INTELUX,
    );
    return;
  }

  // SPFT / Shop Profiling: keep the host global JWT for Angular user-service.
  // The MFE reads the project-scoped JWT from project-tokens.
  if (isStandaloneMfeProject(entry)) {
    const globalToken = localStorage.getItem(AUTH_STORAGE_KEYS.PROJECT_TOKENS_P1);
    if (globalToken) {
      setAuthToken(globalToken);
    }
    localStorage.setItem(AUTH_STORAGE_KEYS.ACTIVE_PROJECT_ID, String(projectId));
    window.location.href = toAppHref(
      resolveProjectAppPath(entry) || appPath || DEFAULT_APP_PATHS.INTELUX,
    );
    return;
  }

  setAuthToken(entry.token);
  localStorage.setItem(AUTH_STORAGE_KEYS.ACTIVE_PROJECT_ID, String(projectId));
  window.location.href = toAppHref(
    resolveProjectAppPath(entry) || appPath || DEFAULT_APP_PATHS.INTELUX,
  );
}

/**
 * After shared OTP login, send the user to the correct app based on project access.
 * - Single project → that project's appPath
 * - Multiple projects → always IntElux / project 1 default route (switcher available)
 */
export function resolvePostLoginAppPath(
  projects: ProjectTokenEntry[],
  options?: { preferPmRoute?: boolean; pmRoute?: string; redirectUrl?: string | null },
): string {
  const redirectUrl = options?.redirectUrl?.trim();
  if (
    redirectUrl &&
    redirectUrl !== '/' &&
    !redirectUrl.endsWith('/login') &&
    !redirectUrl.includes('/login?')
  ) {
    return redirectUrl;
  }

  // Multi-project users always land on IntElux first (ignore preferPmRoute
  // and any PM redirect from a previous session).
  if (projects.length > 1) {
    if (redirectUrl && !redirectUrl.includes('/project-management/')) {
      return redirectUrl;
    }
    const intelux = projects.find(isInteluxProject);
    if (intelux) {
      return resolveProjectAppPath(intelux);
    }
    return DEFAULT_APP_PATHS.INTELUX;
  }

  if (options?.preferPmRoute && options.pmRoute) {
    return options.pmRoute;
  }

  if (!projects.length) {
    return DEFAULT_APP_PATHS.INTELUX;
  }

  if (projects.length === 1) {
    return resolveProjectAppPath(projects[0]);
  }

  const intelux = projects.find(isInteluxProject);
  if (intelux) {
    return resolveProjectAppPath(intelux);
  }

  return DEFAULT_APP_PATHS.INTELUX;
}

/**
 * Clear host session without wiping unrelated keys (feature flags, UI prefs, etc.).
 */
export function clearHostSession(): void {
  for (const key of HOST_SESSION_KEYS) {
    localStorage.removeItem(key);
  }
  sessionStorage.removeItem('postLoginRoute');
}

export function isMicroFrontendHosted(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const win = window as Window & {
    __INSITESTORE_MFE_HOSTED__?: boolean;
  };
  return win.__INSITESTORE_MFE_HOSTED__ === true;
}

export function redirectToHostLogin(): void {
  // Host Angular app serves login at `/` (not `/login`). Using `/login` hits the
  // wildcard → `/` → LoginGuard, which fights SPFT routing and reloads the page.
  const loginHref = '/';
  if (typeof window === 'undefined') {
    return;
  }
  const path = window.location.pathname;
  if (path === '/' || path === '') {
    return;
  }
  // One-shot guard: hard navigation + LoginGuard previously caused a reload loop.
  try {
    if (sessionStorage.getItem('host-login-redirect-lock') === '1') {
      return;
    }
    sessionStorage.setItem('host-login-redirect-lock', '1');
  } catch {
    // ignore storage errors
  }
  if (window.top && window.top !== window.self) {
    window.top.location.replace(loginHref);
    return;
  }
  window.location.replace(loginHref);
}
